import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import {
  ChevronsRightIcon,
  Edit,
  Minus,
  Plus,
  Trash,
  Download,
} from "lucide-react";
import {
  AddButton,
  CustomConfirmModal,
  PaginationDropdown,
  ThinDropdownWithSearch,
} from "../../common/GeneralComponent";
import { fetchData } from "../../../utils/functions";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Pagination,
} from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../../components/partials/Loader";
import * as XLSX from "xlsx";

import GeneralFooter from "../../common/GeneralFooter";
import PermissionButton from "../../common/PermissionButton";

const RGC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

    const { user: loggedInUser } = useSelector((state) => state.auth);
    const permissions = loggedInUser?.user_permissions || [];


  const [search, setSearch] = useState("");
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [tempDistrict, setTempDistrict] = useState(null);

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });
  const [loader, setLoader] = useState(true);
  const [exportLoader, setExportLoader] = useState(false);

  const fetchDistricts = async () => {
    try {
      const response = await fetchData("districts", "GET");
      setDistricts(response?.data || []);
    } catch (error) {
      console.error("Error fetching districts:", error);
      toast.error("Error fetching districts");
    }
  };

  const fetchRGCs = async () => {
    try {
      setLoader(true);
      const queryParams = {
        search: search || undefined,
        page: pagination.currentPage,
        limit: pagination.pageSize,
        district_id: selectedDistrict || undefined,
      };

      const response = await fetchData("rgcs", "GET", null, {}, queryParams);

      setData(response?.data || []);
      setPagination(response?.pagination || {});
      setLoader(false);
    } catch (error) {
      console.error("Error fetching RGCs:", error);
      toast.error("Error fetching RGCs");
      setLoader(false);
    }
  };
  const exportToExcel = async () => {
    try {
      setExportLoader(true);
      const queryParams = {
        search: search || undefined,
        district_id: selectedDistrict || undefined,
      };

      const response = await fetchData("rgcs", "GET", null, {}, queryParams);
      const exportData = response?.data || [];
      const excelData = exportData.map((rgc, index) => ({
        "Sr.No": index + 1,
        "RGC Name": rgc.rgc_name || "-",
        "District Name":
          rgc?.District?.District_Name || rgc?.district?.district_name || "-",
        Status:
          rgc.status === 1 ? "Active" : rgc.status === 0 ? "In-Active" : "-",
        "Created Date": rgc.created_at
          ? new Date(rgc.created_at).toLocaleDateString()
          : "-",
        "Updated Date": rgc.updated_at
          ? new Date(rgc.updated_at).toLocaleDateString()
          : "-",
      }));
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      const columnWidths = [
        { wch: 8 },
        { wch: 25 }, 
        { wch: 20 }, 
        { wch: 12 }, 
        { wch: 15 },
        { wch: 15 }, 
      ];
      worksheet["!cols"] = columnWidths;

      XLSX.utils.book_append_sheet(workbook, worksheet, "RGCs");
      let filename = `RGCs_${new Date().toISOString().split("T")[0]}`;
      if (search) filename += `_search_${search}`;
      if (selectedDistrict) {
        const districtName =
          districts.find(
            (d) => (d.district_id || d.District_Id) === selectedDistrict
          )?.district_name || selectedDistrict;
        filename += `_district_${districtName}`;
      }
      filename += ".xlsx";
      XLSX.writeFile(workbook, filename);

      toast.success(`${exportData.length} RGCs exported successfully!`);
      setExportLoader(false);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Error exporting data to Excel");
      setExportLoader(false);
    }
  };

  useEffect(() => {
    fetchDistricts();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (pagination.currentPage !== 1) {
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
      } else {
        fetchRGCs();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [search, selectedDistrict]);

  useEffect(() => {
    fetchRGCs();
  }, [pagination.currentPage, pagination.pageSize]);

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, currentPage: value }));
  };

  const handleDelete = async () => {
    setModalVisible(false);
    setData((prev) => prev.filter((rgc) => rgc.rgc_id !== selectedId));

    try {
      const response = await fetchData("rgcs", "DELETE", {
        rgc_id: selectedId,
      });

      if (!response?.success) {
        throw new Error(response?.message || "Failed to delete the RGC.");
      }
      toast.success("RGC deleted successfully.");
    } catch (error) {
      console.error("Error deleting RGC:", error);
      toast.error(
        error?.message || "An error occurred while deleting the RGC."
      );
      fetchRGCs();
    }
  };

  const showDeleteModal = (rgcId) => {
    setSelectedId(rgcId);
    setModalVisible(true);
  };

  const handlePageSizeChange = (size) => {
    setPagination((prev) => ({ ...prev, pageSize: size, currentPage: 1 }));
  };

  const handleSearchChange = (e) => {
    const input = e.target.value.toLowerCase();
    if (input === "active") {
      setSearch("1");
    } else if (input === "In-Active") {
      setSearch("0");
    } else {
      setSearch(input);
    }
  };

  const handleDistrictChange = (selected) => {
    setSelectedDistrict(selected);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const toggleFilters = () => {
    setIsFiltersVisible((prev) => !prev);
  };
  const clearFilters = () => {
    setSearch("");
    setTempDistrict(null);
    setSelectedDistrict(null);
    setDistricts([]);
    fetchDistricts();
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const columns = [
    {
      name: <span className="font-[Inter] text-[0.813rem]">RGC Name</span>,
      selector: (row) => row.rgc_name,
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">District Name</span>,
      selector: (row) =>
        row?.District?.District_Name || row?.district?.district_name || "-",
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">Status</span>,
      cell: (row) => {
        const status = row.status;
        if (status === null || status === undefined) {
          return <span className="text-gray-400">-</span>;
        }

        const isActive = status === 1;

        return (
          <span
            className={`py-1 px-2 text-[0.85em] rounded ${
              isActive ? "bg-success text-white" : "bg-red-500 text-white"
            }`}
          >
            {isActive ? "Active" : "In-Active"}
          </span>
        );
      },
    },
  ];
  {
    if (
      permissions.includes("rgc-update") ||
      permissions.includes("rgc-delete")
    ) {
      columns.push({
        name: <span className="text-[0.813rem]">Action</span>,
        cell: (row) => (
          <div className="flex gap-2">
            {/* <div className="flex gap-2">
          <Link to={`/rgcs/edit/${row.rgc_id}`}>
            <i className="fa-solid fa-pen-to-square fa-lg"></i>
          </Link>
        </div>
        <button onClick={() => showDeleteModal(row.rgc_id)}>
          <i
            class="fa fa-trash fa-lg text-primaryDark"
            aria-hidden="true"
          ></i>
        </button> */}

            <PermissionButton
              hasPermission={permissions.includes("rgc-update")}
              to={`/rgcs/edit/${row.rgc_id}`}
              faIcon="fa-solid fa-pen-to-square fa-lg"
              onlyIcon={true}
              enabledClass="" 
              disabledClass="opacity-50 cursor-not-allowed"
            />
            <PermissionButton
              hasPermission={permissions.includes("rgc-delete")}
              faIcon="fa fa-trash fa-lg text-primaryDark"
              onlyIcon
              onClick={() => showDeleteModal(row.rgc_id)}
              className="p-1 rounded-full bg-gray-200 text-[#137e81] hover:text-white"
              enabledClass=""
              disabledClass="opacity-50 cursor-not-allowed bg-gray-300"
            />
          </div>
        ),
      });
    }
}

  const customStyles = {
    headCells: {
      style: {
        border: "1px solid #f3f3f3",
        backgroundColor: "",
        color: "black",
        fontWeight: "bold",
        fontSize: "1.075rem",
        textAlign: "center",
      },
    },
    cells: {
      style: {
        border: "1px solid #f3f3f3",
        textAlign: "center",
      },
    },
  };

  return (
    <div>
      {loader && <Loader />}
      <div className="my-4 w-full flex flex-col justify-between space-y-3">
        <div className="flex  lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
          <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
            RGC
          </h1>
          <div className="flex gap-2">
            {data.length > 0 && (
              <button
                onClick={exportToExcel}
                disabled={exportLoader}
                className="flex items-center gap-2 bg-[#137e81] text-sm text-white px-4 py-1 rounded-md hover:bg-[#0f6a6d] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {exportLoader ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    Export Excel
                  </>
                )}
              </button>
            )}
            {/* <Link to="add">
              <AddButton text="Add" icon={ChevronsRightIcon} />
            </Link> */}

            <PermissionButton
              hasPermission={permissions.includes("rgc-create")}
              to="add"
              faIcon="fa fa-plus"
              text="Add"
              className="flex items-center gap-2 py-[0.375rem] px-[0.75rem] rounded font-[InterLight] lg:text-[15px] text-[12px]"
              enabledClass="bg-[#be9609] text-white w-auto opacity-95 hover:opacity-85"
              disabledClass="bg-[#be9609] text-white opacity-50 cursor-not-allowed"
            />
          </div>
        </div>

        <Accordion>
          <AccordionSummary
            expandIcon={
              isFiltersVisible ? (
                <Minus color="#ffff" size={14} />
              ) : (
                <Plus size={14} color="#ffff" />
              )
            }
            aria-controls="panel1-content"
            id="panel1-header"
            onClick={toggleFilters}
            sx={{
              backgroundColor: "#137e81",
              color: "#fff !important", 
              "& .MuiAccordionSummary-content": {
                color: "#fff  !important", 
              },
            }}
          >
            Filters
          </AccordionSummary>

          {/* <AccordionDetails>
            <div className="flex gap-5 w-full">
              <div className="w-[30%]">
                <ThinDropdownWithSearch required={false}
                  label="District"
                  id="district-dropdown"
                  options={districts.map((district) => ({
                    value: district.district_id || district.District_Id,
                    label: district.district_name || district.District_Name,
                  }))}
                  value={selectedDistrict}
                  onChange={handleDistrictChange}
                  required={false}
                />
              </div>
              <div className="w-[30%] flex items-end">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-[#be9609] text-white rounded hover:bg-gray-600 transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>
          </AccordionDetails> */}

          <AccordionDetails>
            <div className="flex gap-5 w-full">
              <div className="w-[30%]">
                <ThinDropdownWithSearch required={false}
                  label="District"
                  id="district-dropdown"
                  options={districts.map((district) => ({
                    value: district.district_id || district.District_Id,
                    label: district.district_name || district.District_Name,
                  }))}
                  value={tempDistrict}
                  onChange={(value) => setTempDistrict(value)}
                />
              </div>

              <div className="flex gap-3 items-end">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-[#be9609] text-white rounded hover:bg-gray-600 transition-colors"
                >
                  Clear
                </button>

                <button
                  onClick={() => {
                    setSelectedDistrict(tempDistrict);
                    setPagination((prev) => ({ ...prev, currentPage: 1 }));
                  }}
                  className="px-4 py-2 bg-[#137e81] text-white rounded hover:bg-[#0f6b6e] transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>
          </AccordionDetails>
        </Accordion>

        {/* <br /> */}
        <div className="bg-white rounded-lg shadow w-full lg:mb-[30px] mt-4  mb-[7rem]">
          <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b ">
            <h1 className="lg:text-[0.875rem] text-[13px]  tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
              RGC List
            </h1>
          </div>
          <div className="p-[1.25rem] w-full">
            <div className="flex lg:flex-row flex-col lg:justify-between justify-start gap-2 lg:items-center items-start mb-4 w-full">
              <PaginationDropdown
                value={pagination.pageSize}
                onChange={handlePageSizeChange}
                options={[
                  { value: 10, label: "10" },
                  { value: 20, label: "20" },
                  { value: 30, label: "30" },
                ]}
              />

              <input
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                type="search"
                className="text-[Light] flex items-center bg-[#ebeef2] border-[#ebeef2] rounded-md px-3 py-1 outline-none"
              />
            </div>

            <DataTable
              columns={columns}
              persistTableHead
              data={data}
              customStyles={customStyles}
              highlightOnHover
            />

            <GeneralFooter
              pagination={pagination}
              handlePageChange={handlePageChange}
              handlePageSizeChange={handlePageSizeChange}
              dataLength={data.length}
            />

            {/* <div className="flex md:flex-row flex-col justify-between items-center">
              <div className="flex gap-2 justify-between items-center my-4 text-[1rem] text-[#212529]">
                <p className="font-[Regular]">
                  Page:{" "}
                  <strong className="font-[Inter]">
                    {pagination.currentPage}
                  </strong>{" "}
                  of{" "}
                  <strong className="font-[Inter]">
                    {pagination.totalPages}
                  </strong>
                </p>
              </div>
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage}
                onChange={handlePageChange}
                shape="rounded"
                sx={{
                  "& .Mui-selected": {
                    backgroundColor: "none",
                    color: "#137e81",
                    borderBottom: "2px solid #137e81",
                    "&:hover": {
                      color: "#fff",
                      backgroundColor: "#137e81",
                    },
                  },
                }}
              />
            </div> */}
          </div>
        </div>

        <CustomConfirmModal
          isVisible={isModalVisible}
          onConfirm={handleDelete}
          onCancel={() => setModalVisible(false)}
          message="Are you sure you want to delete this RGC?"
        />
      </div>
    </div>
  );
};

export default RGC;
